import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Pause, Play, RefreshCw } from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import { Select } from '../components/Controls';
import { Chip } from './attendance/shared';
import { dt, every } from './biometric/shared';

// Time & Attendance > Biometric Registrations > Device Log: every request the receiver got, as received (biometric
// templates withheld). This is where the real device's protocol is read and the adapter's codes are confirmed.

const KINDS = [['', 'All Requests'], ['handshake', 'Handshakes'], ['upload', 'Uploads (punches, operations)'], ['heartbeat', 'Heartbeats'],
  ['command', 'Command Results'], ['other', 'Other Endpoints']];
const kindOf = r => (r.path === '/iclock/cdata' ? (r.method === 'GET' ? 'Handshake' : 'Upload') : r.path === '/iclock/getrequest' ? 'Heartbeat'
  : r.path === '/iclock/devicecmd' ? 'Command Result' : 'Other');

export default function BiometricDeviceLog() {
  const { notify } = useHR();
  const [f, setF] = useState({ sn: '', kind: '', refused: false, limit: 200 });
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [live, setLive] = useState(false);
  const load = useCallback(() => hr.getDeviceLog(f).then(r => { setD(r); setError(''); }).catch(e => setError(e.message)), [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => (live ? every(5000, load) : undefined), [live, load]);

  if (error && !d) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading the device log…" subtitle="Fetching requests from the devices" />;
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  return (
    <div className="mdr mds ael bio bdv">
      <div className="mdr-top">
        <div>
          <h2>Device Log</h2>
          <p>Every request the receiver got from the devices, as it arrived. Fingerprint and face templates are never shown or stored here.</p>
        </div>
        <div className="mdr-top-actions no-print">
          <button className={`mdr-btn ${live ? 'bdv-live-on' : ''}`} onClick={() => setLive(v => !v)}>{live ? <><span className="bdv-dot" /> <Pause size={13} /> Following</> : <><Play size={13} /> Follow</>}</button>
          <button className="mdr-btn" onClick={() => load().then(() => notify('Log refreshed'))}><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="mdr-card mdr-filters no-print">
        <Select value={f.sn} onChange={e => set('sn', e.target.value)}><option value="">All Serial Numbers</option>{d.serials.map(s => <option key={s} value={s}>{s}</option>)}</Select>
        <Select value={f.kind} onChange={e => set('kind', e.target.value)}>{KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select>
        <Select value={String(f.limit)} onChange={e => set('limit', Number(e.target.value))}>{[100, 200, 500, 1000].map(n => <option key={n} value={n}>Latest {n}</option>)}</Select>
        <label className="bdv-check"><input type="checkbox" checked={f.refused} onChange={e => set('refused', e.target.checked)} /> Refused only</label>
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table ts-table bdv-log">
            <thead><tr><th /><th>Received</th><th>Serial No.</th><th>Request</th><th>From IP</th><th>Reply</th><th className="c">Bytes</th><th>Note</th></tr></thead>
            <tbody>
              {d.rows.map(r => (
                <Fragment key={r.id}>
                  <tr className="mdr-row bdv-log-row" onClick={() => setOpen(o => ({ ...o, [r.id]: !o[r.id] }))}>
                    <td>{open[r.id] ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</td>
                    <td className="nowrap"><small>{dt(r.at)}</small></td>
                    <td className="mono nowrap">{r.serial_no || '—'}</td>
                    <td className="nowrap"><b>{kindOf(r)}</b> <small className="mono muted">{r.method} {r.path}</small></td>
                    <td className="mono nowrap">{r.remote_ip || '—'}</td>
                    <td><Chip tone={r.status_code < 400 ? 'green' : 'red'}>{r.status_code}</Chip></td>
                    <td className="c">{r.body_bytes || ''}</td>
                    <td><small>{r.note || ''}</small></td>
                  </tr>
                  {open[r.id] && (
                    <tr className="bdv-log-open"><td /><td colSpan={7}>
                      <div className="bdv-raw">
                        <div><small>Query</small><pre>{r.query || '(none)'}</pre></div>
                        <div><small>Body</small><pre>{r.body || '(empty)'}</pre></div>
                        <div><small>Our reply</small><pre>{r.response || ''}</pre></div>
                      </div>
                    </td></tr>
                  )}
                </Fragment>
              ))}
              {!d.rows.length && <tr><td colSpan={8} className="mdr-empty">No requests yet. Point a device at the receiver and its handshake will show here.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="ael-pager"><span>{d.rows.length} request(s){live ? ' · following every 5 seconds' : ''}</span></div>
      </div>
    </div>
  );
}
